import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import api, { publicImg } from "../core/api";
import { fa, formatLongDate, todayIso } from "../core/format";
import Logo from "./Logo";

let pdfLibPromise = null;
const loadPdfLib = () => {
  if (!pdfLibPromise)
    pdfLibPromise = import("pdfjs-dist").then((m) => {
      const lib = m.getDocument ? m : m.default;
      lib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
      return lib;
    });
  return pdfLibPromise;
};

const MAX_PDF_PAGES = 40;
async function pdfToImages(blob) {
  const lib = await loadPdfLib();
  const pdf = await lib.getDocument({ data: await blob.arrayBuffer() }).promise;
  const out = [];
  const n = Math.min(pdf.numPages, MAX_PDF_PAGES);
  for (let i = 1; i <= n; i++) {
    const page = await pdf.getPage(i);
    const vp = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    canvas.width = vp.width;
    canvas.height = vp.height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    out.push(canvas.toDataURL("image/jpeg", 0.9));
    canvas.width = canvas.height = 0;
  }
  return out;
}

const chunk2 = (arr) => {
  const o = [];
  for (let i = 0; i < arr.length; i += 2) o.push(arr.slice(i, i + 2));
  return o;
};

/**
 * props:
 *  title, docNo
 *  meta:       [[label, value], ...]               مشخصات (دو ستونه)
 *  blocks:     [[label, text], ...]                متن‌های بلند
 *  tables:     [{ title, head: [], rows: [[]] }]   جدول‌ها
 *  signatures: [string]                            کادر امضا
 *  files:      [{ path, name, optional? }]         پیوست‌ها (هرکدام یک صفحه‌ی A4)
 *  load:       async () => ({ ...هرکدام از موارد بالا })   (نتیجه روی مقادیر اولیه می‌نشیند)
 */
export default function PrintReport(props) {
  const { onClose } = props;
  const base = useRef(props).current;
  const [org, setOrg] = useState(null);
  const [data, setData] = useState({ ...base });
  const [pages, setPages] = useState([]);
  const [status, setStatus] = useState("loading");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [warns, setWarns] = useState([]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTitle = document.title;
    document.body.style.overflow = "hidden";
    document.title = base.title || "گزارش";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.title = prevTitle;
    };
  }, [base.title]);
useEffect(() => {
  const prevOverflow = document.body.style.overflow;
  const prevTitle = document.title;
  document.body.style.overflow = "hidden";
  document.body.classList.add("pr-open");          // ← اضافه
  document.title = base.title || "گزارش";
  return () => {
    document.body.style.overflow = prevOverflow;
    document.body.classList.remove("pr-open");     // ← اضافه
    document.title = prevTitle;
  };
}, [base.title]);
  useEffect(() => {
    let dead = false;
    const urls = [];
    (async () => {
      const warnings = [];
      const [orgRes, extra] = await Promise.all([
        api.get("/org").then((r) => r.data.org).catch(() => null),
        base.load
          ? base.load().catch(() => {
              warnings.push("بخشی از اطلاعات سند بارگذاری نشد");
              return {};
            })
          : {},
      ]);
      if (dead) return;
      const merged = { ...base, ...(extra || {}) };
      setOrg(orgRes);
      setData(merged);
      const list = merged.files || [];
      setProgress({ done: 0, total: list.length });
      const out = [];
      for (let i = 0; i < list.length; i++) {
        const f = list[i];
        try {
          const { data: blob } = await api.get(f.path, { responseType: "blob", timeout: 120000 });
          const t = blob.type || "";
          if (t.startsWith("image/")) {
            const u = URL.createObjectURL(blob);
            urls.push(u);
            out.push({ src: u, name: f.name });
          } else if (t === "application/pdf") {
            const imgs = await pdfToImages(blob);
            imgs.forEach((src, k) =>
              out.push({ src, name: f.name, part: imgs.length > 1 ? `صفحه ${fa(k + 1)} از ${fa(imgs.length)}` : "" }),
            );
          } else if (!f.optional) warnings.push(`«${f.name}»: نوع فایل قابل چاپ نیست`);
        } catch {
          if (!f.optional) warnings.push(`«${f.name}» بارگذاری نشد`);
        }
        if (dead) return;
        setProgress({ done: i + 1, total: list.length });
      }
      if (dead) return;
      setPages(out);
      setWarns(warnings);
      setStatus("ready");
    })();
    return () => {
      dead = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [base]);

  const doPrint = async () => {
    const imgs = [...document.querySelectorAll(".pr-overlay img")];
    await Promise.all(imgs.map((i) => (i.decode ? i.decode().catch(() => {}) : null)));
    window.print();
  };

  const { title, docNo } = data;
  const meta = (data.meta || []).filter(Boolean);
  const blocks = (data.blocks || []).filter(([, t]) => t && String(t).trim());
  const tables = (data.tables || []).filter((t) => t.rows?.length);
  const sigs = data.signatures || [];

  return ReactDOM.createPortal(
    <div className="pr-overlay" dir="rtl">
      <div className="pr-bar">
        <b>پیش‌نمایش چاپ — {title}</b>
        <span className="pr-status">
          {status === "loading"
            ? `در حال آماده‌سازی فایل‌ها... ${fa(progress.done)} از ${fa(progress.total)}`
            : `${fa(1 + pages.length)} صفحه A4`}
        </span>
        <span style={{ flex: 1 }} />
        <button className="btn btn-sm" onClick={doPrint} disabled={status !== "ready"}>
          <i className="fa-solid fa-print" /> چاپ
        </button>
        <button className="btn btn-sm btn-ghost" onClick={onClose}>بستن</button>
      </div>
      {warns.length > 0 && (
        <div className="pr-warn">
          {warns.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}

      <div className="pr-sheet">
        {/* صفحه‌ی اول: توضیحات */}
        <section className="pr-page pr-flow">
          <header className="pr-head">
            <div className="pr-logo">
              {org?.orgLogo ? <img src={publicImg("org", org.orgLogo)} alt="" /> : <Logo size={60} />}
            </div>
            <div className="pr-org">
              <b>{org?.orgName || "بندر تجاری و مسافری چارک"}</b>
              <span>{org?.unitName}</span>
            </div>
            <div className="pr-doc">
              <span>تاریخ چاپ: {formatLongDate(todayIso())}</span>
              {docNo && <span>شماره سند: {docNo}</span>}
            </div>
          </header>

          <h1 className="pr-title">{title}</h1>

          {meta.length > 0 && (
            <table className="pr-meta">
              <tbody>
                {chunk2(meta).map((row, i) => (
                  <tr key={i}>
                    {row.map(([l, v], j) => (
                      <React.Fragment key={j}>
                        <th>{l}</th>
                        <td colSpan={row.length === 1 ? 3 : 1}>{v === null || v === undefined || v === "" ? "—" : v}</td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {blocks.map(([l, t], i) => (
            <div className="pr-block" key={i}>
              <h4>{l}</h4>
              <p>{t}</p>
            </div>
          ))}

          {tables.map((t, i) => (
            <div className="pr-block" key={i}>
              <h4>{t.title}</h4>
              <table className="pr-tbl">
                <thead><tr>{t.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {t.rows.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j}>{c ?? "—"}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          ))}

          {status === "ready" && (
            <div className="pr-block">
              <h4>پیوست‌ها</h4>
              <p>{pages.length ? `${fa(pages.length)} صفحه پیوست در ادامه آمده است.` : "فایل پیوستی ثبت نشده است."}</p>
            </div>
          )}

          {sigs.length > 0 && (
            <div className="pr-block">
              <h4>امضاها</h4>
              <div className="pr-sign">
                {sigs.map((s, i) => <div key={i}><b>{s}</b><span>امضاء:</span></div>)}
              </div>
            </div>
          )}
        </section>

        {/* صفحه‌های پیوست: هر فایل / هر صفحه‌ی PDF یک صفحه */}
        {pages.map((p, i) => (
          <section className="pr-page pr-att" key={i}>
            <div className="pr-att-head">
              <span>پیوست {fa(i + 1)} از {fa(pages.length)}</span>
              <b>{p.name}{p.part ? ` — ${p.part}` : ""}</b>
              <span>{title}{docNo ? ` · ${docNo}` : ""}</span>
            </div>
            <div className="pr-att-body"><img src={p.src} alt={p.name} /></div>
          </section>
        ))}
      </div>
    </div>,
    document.body,
  );
}